/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { Icon, ICON_SIZES, StackView, STACK_ALIGN, STACK_GAP, Text, TEXT_SIZE } from '@veeam-vspc/components';
import { ProtectedDataFilterBy, VbrWorkloads } from '@veeam-vspc/models/web-controllers';
import { observer } from 'mobx-react-lite';

import type { BaseProtectedDataWorkloadOverviewVbrWorkloads, ProtectedDataOverviewRequest } from '@veeam-vspc/models/web-controllers';

import { TableWidget } from 'views/components/TableWidget';
import { NameColumn } from 'views/components/TableWidget/components/NameColumn';
import successIcon from 'images/success.svg';
import serverIcon from 'images/pages/protected-data-summary/cc-server.svg';
import vmIcon from 'images/pages/protected-data-summary/cc-vm.svg';
import workstationIcon from 'images/pages/protected-data-summary/cc-workstation.svg';
import fileShareIcon from 'images/pages/protected-data-summary/file-share.svg';
import { useLang } from 'views/providers/LangProvider/hooks';
import { useIntervalRequest } from 'views/hooks';
import { EmptyColumnPlaceholder } from 'views/components/TableWidget/components/EmptyColumnPlaceholder';
import { NoInfoColumn } from 'views/components/TableWidget/components/NoInfoColumn';
import vbrNotConnected from 'images/vbr-not-connected.svg';
import { useProtectedDataSummaryStore } from '../../store';

const icons = {
    [VbrWorkloads.Servers]: serverIcon,
    [VbrWorkloads.FileShares]: fileShareIcon,
    [VbrWorkloads.VirtualMachines]: vmIcon,
    [VbrWorkloads.Workstations]: workstationIcon,
};

export const VBR: React.FC = observer(() => {
    const store = useProtectedDataSummaryStore();
    const lang = useLang();
    const didMount = useRef(false);
    const abortController = useRef(new AbortController());

    const names = useMemo(() => ({
        [VbrWorkloads.Servers]: lang.SERVERS,
        [VbrWorkloads.FileShares]: lang.FILE_SHARES,
        [VbrWorkloads.VirtualMachines]: lang.VIRTUAL_MACHINES,
        [VbrWorkloads.Workstations]: lang.WORKSTATIONS,
    }), []);

    const request = useIntervalRequest<ProtectedDataOverviewRequest, BaseProtectedDataWorkloadOverviewVbrWorkloads[]>(
        '/ProtectedOverview/OnPremises',
        {
            rpoDays: store.rpoDays,
            filterType: ProtectedDataFilterBy.Rpo,
        },
        {
            signal: abortController.current.signal,
        });

    useEffect(() => {
        if (didMount.current) {
            abortController.current.abort();

            abortController.current = new AbortController();
            return request.forceRequest();
        }

        didMount.current = true;
    }, [store.rpoDays]);

    const items = request.isError || !Array.isArray(request.data)
        ? []
        : (request.data as BaseProtectedDataWorkloadOverviewVbrWorkloads[]);

    return (
        <TableWidget
            data={items}
            columns={[
                {
                    name: lang.VEEAM_BACKUP_AND_REPLICATION,
                    cellRenderer: ({ rowData, key }) => (
                        <NameColumn
                            icon={icons[rowData.workloadType]}
                            name={names[rowData.workloadType]}
                            key={key}
                        />
                    ),
                },
                {
                    cellRenderer: ({ key }) => <EmptyColumnPlaceholder key={key} />,
                },
                {
                    cellRenderer: ({ rowData, key }) => {
                        const protectedCount = rowData.protectionData.reduce((acc, item) => acc + item.protectedCount.value, 0);
                        const overallCount = rowData.protectionData.reduce((acc, item) => acc + item.overallCount, 0);

                        if (overallCount === 0) {
                            return (
                                <StackView
                                    align={STACK_ALIGN.center}
                                    gap={STACK_GAP.s}
                                    key={key}
                                >
                                    <NoInfoColumn />
                                </StackView>
                            );
                        }

                        if (protectedCount !== overallCount) {
                            return (
                                <StackView
                                    key={key}
                                    align={STACK_ALIGN.end}
                                    gap={STACK_GAP.s}
                                >
                                    <Text size={TEXT_SIZE.xl}>{protectedCount}</Text>
                                    <Text size={TEXT_SIZE.l}>of</Text>
                                    <Text size={TEXT_SIZE.xl}>{overallCount}</Text>
                                </StackView>
                            );
                        }

                        return (
                            <StackView
                                key={key}
                                align={STACK_ALIGN.center}
                                gap={STACK_GAP.s}
                            >
                                <Icon src={successIcon} size={ICON_SIZES.xl} />
                                <Text size={TEXT_SIZE.xl}>{overallCount}</Text>
                            </StackView>
                        );
                    },
                },
            ]}
            loading={request.loading}
            noDataConfig={{
                linkText: lang.START_MANAGING_VEEAM_BACKUP_AND_REPLICATION,
                icon: vbrNotConnected,
                linkHandler: () => window.location.assign('#home/discovery/tab/discoveredComputersTab/s/discoveredComputers'),
            }}
        />
    );
});
